import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getSSectionVO,
    saveSSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    S2_PAGE,
    S3_PAGE,
    SS_PAGE,
    S_PAGE_TITLES,
    S_SECTION_INDEX,
    S_SECTION_INDEX_LETTER,
    S_SECTION_INDEX_TITLE,
    S_SECTION_PAGES,
    S3Page,
    SProgrammePage,
    SSectionPageCode,
    SSummaryPage,
} from '@/pages/components/audit-program/s-section/constants';
import SsSummaryView from '@/pages/components/audit-program/s-section/SsSummaryView';
import S3IceView from '@/pages/components/audit-program/s-section/S3IceView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type SSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: SSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<SSectionPageCode>(['Si']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function SSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Si',
}: SSectionViewProps) {
    const [pageKey, setPageKey] = useState<SSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('S');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getSSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of S_SECTION_PAGES) {
                if (code === 'Si') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load S Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: SSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveSSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Si') as {rows?: typeof S_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : S_SECTION_INDEX;
    }, [getContent]);

    const ssContent = (getContent('Ss') ?? SS_PAGE) as SSummaryPage;
    const s2Content = (getContent('S2') ?? S2_PAGE) as SProgrammePage;
    const s3Content = (getContent('S3') ?? S3_PAGE) as S3Page;

    const tabs = useMemo(() => S_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={S_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Si' ? (
                <SectionIndexView
                    header={header}
                    refCode="Si"
                    sectionLetter={S_SECTION_INDEX_LETTER}
                    sectionTitle={S_SECTION_INDEX_TITLE}
                    rows={indexRows}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as SSectionPageCode))}
                />
            ) : code === 'Ss' ? (
                <SsSummaryView
                    header={header}
                    page={ssContent}
                    refCode="Ss"
                    values={pageData.Ss ?? {}}
                    onChange={(k, v) => handleFieldChange('Ss', k, v)}
                />
            ) : code === 'S3' ? (
                <S3IceView
                    header={header}
                    page={s3Content}
                    refCode="S3"
                    values={pageData.S3 ?? {}}
                    onChange={(k, v) => handleFieldChange('S3', k, v)}
                />
            ) : (
                <G0ProgrammeView
                    header={header}
                    page={s2Content}
                    refCode="S2"
                    values={pageData.S2 ?? {}}
                    onChange={(k, v) => handleFieldChange('S2', k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, indexRows, ssContent, s2Content, s3Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    S Section — Operating effectiveness of controls
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as SSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
